import {
  MyThemeProvider,
  TranslationProvider,
} from "@contexts/contextProviders.jsx";
import { ErrorBoundary } from "@features/common/boundaries/ErrorBoundary.jsx";
import ErrorFallback from "@features/common/ui/ErrorFallback.jsx";
import { PageLoader } from "@features/common/ui/PageLoader.jsx";
import { ReactKeycloakProvider } from "@react-keycloak/web";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { Suspense } from "react";
import { BrowserRouter } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import App from "./App.jsx";
import keycloak from "./config/keycloak.jsx";
import { SELECTED_PROJECT } from "./configuration.jsx";
import { startTokenRefresh, stopTokenRefresh } from "./helpers";

const queryClient = new QueryClient();

function AppWithProviders() {
  return (
    <ReactKeycloakProvider
      authClient={keycloak}
      initOptions={{
        onLoad: "check-sso",
        silentCheckSsoRedirectUri:
          window.location.origin + "/silent-check-sso.html",
        pkceMethod: "S256",
        checkLoginIframe: true,
      }}
      onEvent={(event) => {
        if (event === "onReady" || event === "onAuthSuccess") {
          startTokenRefresh(keycloak);
        }
        if (event === "onAuthLogout") {
          stopTokenRefresh();
        }

        const url = new URL(window.location.href);
        const hasKeycloakQuery =
          url.searchParams.has("state") ||
          url.searchParams.has("session_state") ||
          url.searchParams.has("code") ||
          url.searchParams.has("iss");
        const hasKeycloakHash =
          url.hash && /[#&](iss|state|session_state|code)=/.test(url.hash);

        if (hasKeycloakQuery || hasKeycloakHash) {
          window.history.replaceState({}, document.title, url.pathname);
        }
      }}
    >
      <QueryClientProvider client={queryClient}>
        <MyThemeProvider>
          <ErrorBoundary FallbackComponent={ErrorFallback}>
            <Suspense fallback={<PageLoader />}>
              <BrowserRouter>
                <TranslationProvider>
                  <App />
                </TranslationProvider>
              </BrowserRouter>
              <ReactQueryDevtools
                initialIsOpen={false}
                buttonPosition="bottom-left"
              />
            </Suspense>
          </ErrorBoundary>
        </MyThemeProvider>
      </QueryClientProvider>
      <ToastContainer autoClose={1500} />
    </ReactKeycloakProvider>
  );
}

export default AppWithProviders;
